/**
 * WHICH local bucket holds the user's personal records — and nothing else.
 *
 * Split out of `personalSync.js` on 17.09.2026. Ten modules need this one pure
 * getter, among them the MCP board and graph handlers; `personalSync.js` also
 * starts and stops the sync engine (`personal_migrate_local_scope`,
 * `sync_register_personal`, `sync_unregister_workspace`). While both lived in
 * one file, asking "which scope?" put three engine commands inside the MCP
 * import graph, where the only thing standing between a handler and them was
 * that nobody had imported them yet.
 *
 * This module must stay invoke-free. `mcpAttackSurface.test.js` walks the MCP
 * import graph and fails on any Tauri command it has not been told about, so an
 * `invoke` added here lands in that test — which is the point.
 */
import { useAuthStore } from '../store/authStore';

export const PERSONAL_LOCAL_SCOPE = 'personal:local';

/**
 * The local extension_documents bucket for the user's personal records.
 * Synchronous (used inside recordsContext): `personal:<userId>` when signed
 * in, else the device-local `personal:local`. Independent of the active API
 * workspace by design.
 */
export function personalScopeId() {
  const { isAuthenticated, user } = useAuthStore.getState();
  if (isAuthenticated && user?.id != null) return `personal:${user.id}`;
  return PERSONAL_LOCAL_SCOPE;
}

/** True once the user's personal scope is signed-in (not the local draft one). */
export function isPersonalSynced() {
  return personalScopeId() !== PERSONAL_LOCAL_SCOPE;
}

/**
 * Who is signed in, as a document's `createdBy` (documents redesign 2.3), or
 * null. Lives here, beside the other authStore read, so the documents store
 * (inside the MCP import graph) reaches no new store.
 */
export function signedInAuthor() {
  const user = useAuthStore.getState()?.user;
  const name = user?.name || user?.email || '';
  return name ? { id: user.id ?? null, name } : null;
}
